import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { useToast } from '../hooks/use-toast';
import { BookOpen, Plus, RefreshCw, Users, Clock, Calendar, CheckCircle, Sparkles, Play, FileText, Sun, Cloud, Snowflake, Leaf, Download, Printer, Eye, FileDown, QrCode, CalendarDays, Wand2, Trash2, AlertTriangle, TrendingUp, Target } from 'lucide-react';
import { QRShareButton } from './QRCodeShare';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generate printable HTML for Toolbox Talk
const generatePrintableHTML = (talk, session = null) => {
  const isSession = !!session;
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Toolbox Talk - ${talk.title}</title>
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { 
          font-family: Arial, sans-serif; 
          padding: 20px; 
          max-width: 800px; 
          margin: 0 auto; 
          line-height: 1.5;
          font-size: 11pt;
        }
        .header { 
          text-align: center; 
          border-bottom: 3px solid #1e40af; 
          padding-bottom: 15px; 
          margin-bottom: 20px; 
        }
        .header h1 { color: #1e40af; font-size: 20pt; margin-bottom: 5px; }
        .header h2 { color: #374151; font-size: 14pt; font-weight: normal; }
        .meta-info {
          display: flex;
          justify-content: space-around;
          background: #f8fafc;
          padding: 15px;
          border-radius: 8px;
          margin-bottom: 20px;
        }
        .meta-item { text-align: center; }
        .meta-label { font-size: 10pt; color: #6b7280; }
        .meta-value { font-weight: bold; color: #1e40af; }
        .section { margin-bottom: 25px; }
        .section-title { 
          font-size: 13pt; 
          font-weight: bold; 
          color: #1e40af; 
          border-bottom: 2px solid #e5e7eb; 
          padding-bottom: 8px; 
          margin-bottom: 12px; 
        }
        .topic { 
          padding: 10px 15px; 
          background: #f1f5f9; 
          margin-bottom: 8px; 
          border-radius: 6px;
          border-left: 4px solid #3b82f6;
        }
        .topic-number { 
          display: inline-block;
          width: 24px;
          height: 24px;
          background: #3b82f6;
          color: white;
          border-radius: 50%;
          text-align: center;
          line-height: 24px;
          font-size: 11pt;
          font-weight: bold;
          margin-right: 10px;
        }
        .quiz-card { 
          border: 1px solid #e5e7eb; 
          padding: 15px; 
          margin-bottom: 12px; 
          border-radius: 8px;
          background: #fefce8;
        }
        .quiz-question { font-weight: bold; margin-bottom: 8px; }
        .quiz-answer { color: #15803d; padding-left: 20px; }
        .attendance-table { 
          width: 100%; 
          border-collapse: collapse; 
          margin-top: 10px; 
        }
        .attendance-table th, .attendance-table td { 
          border: 1px solid #d1d5db; 
          padding: 12px; 
          text-align: left; 
        }
        .attendance-table th { background: #f3f4f6; font-weight: bold; }
        .attendance-table td { height: 35px; }
        .signature-row { height: 45px; }
        .notes-box {
          border: 1px solid #d1d5db;
          padding: 15px;
          min-height: 80px;
          border-radius: 8px;
          margin-top: 10px;
        }
        .footer { 
          margin-top: 30px; 
          padding-top: 15px; 
          border-top: 2px solid #e5e7eb; 
          text-align: center;
        }
        .signature-line { 
          border-bottom: 1px solid #000; 
          width: 180px; 
          display: inline-block; 
          margin: 0 10px; 
        }
        .session-info {
          background: #dcfce7;
          border: 1px solid #86efac;
          padding: 15px;
          border-radius: 8px;
          margin-bottom: 20px;
        }
        .session-info h3 { color: #166534; margin-bottom: 10px; }
        @media print {
          body { padding: 10px; }
          .no-print { display: none; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>🛡️ TOOLBOX TALK</h1>
        <h2>${talk.title}</h2>
      </div>

      <div class="meta-info">
        <div class="meta-item">
          <div class="meta-label">Duration</div>
          <div class="meta-value">${talk.duration || 10} minutes</div>
        </div>
        <div class="meta-item">
          <div class="meta-label">Topics</div>
          <div class="meta-value">${talk.topics?.length || 0} items</div>
        </div>
        <div class="meta-item">
          <div class="meta-label">Date</div>
          <div class="meta-value">${session?.date ? new Date(session.date).toLocaleDateString() : new Date().toLocaleDateString()}</div>
        </div>
        ${talk.has_quiz ? `
        <div class="meta-item">
          <div class="meta-label">Quiz</div>
          <div class="meta-value">${talk.quiz_questions?.length || 0} Questions</div>
        </div>
        ` : ''}
      </div>

      ${session ? `
      <div class="session-info">
        <h3>✅ Session Information</h3>
        <p><strong>Presenter:</strong> ${session.presenter}</p>
        <p><strong>Location:</strong> ${session.location}</p>
        <p><strong>Date:</strong> ${new Date(session.date).toLocaleDateString()}</p>
        <p><strong>Attendees:</strong> ${session.attendance_count || session.attendees?.length || 0}</p>
      </div>
      ` : ''}

      <div class="section">
        <div class="section-title">📋 Discussion Topics</div>
        ${talk.topics?.map((topic, i) => `
          <div class="topic">
            <span class="topic-number">${i + 1}</span>
            ${topic}
          </div>
        `).join('') || `<div class="topic">${talk.content || 'No topics available'}</div>`}
      </div>

      ${talk.key_points?.length > 0 ? `
      <div class="section">
        <div class="section-title">🎯 Key Points to Remember</div>
        ${talk.key_points.map((point, i) => `
          <div class="topic">• ${point}</div>
        `).join('')}
      </div>
      ` : ''}

      ${talk.quiz_questions?.length > 0 ? `
      <div class="section">
        <div class="section-title">❓ Quiz Questions</div>
        ${talk.quiz_questions.map((q, i) => `
          <div class="quiz-card">
            <div class="quiz-question">Q${i + 1}: ${q.question}</div>
            <div class="quiz-answer">✓ ${q.answer}</div>
          </div>
        `).join('')}
      </div>
      ` : ''}

      <div class="section">
        <div class="section-title">✍️ Attendance Sign-In Sheet</div>
        <table class="attendance-table">
          <thead>
            <tr>
              <th style="width: 5%">#</th>
              <th style="width: 30%">Name</th>
              <th style="width: 20%">Employee ID</th>
              <th style="width: 25%">Signature</th>
              <th style="width: 20%">Date</th>
            </tr>
          </thead>
          <tbody>
            ${session?.attendees?.map((a, i) => `
              <tr>
                <td>${i + 1}</td>
                <td>${a.employee_name}</td>
                <td>${a.employee_id || '-'}</td>
                <td>✓</td>
                <td>${new Date(session.date).toLocaleDateString()}</td>
              </tr>
            `).join('') || ''}
            ${/* Generate empty rows for sign-in */ [1,2,3,4,5,6,7,8,9,10].slice(session?.attendees?.length || 0).map(i => `
              <tr class="signature-row">
                <td>${(session?.attendees?.length || 0) + i}</td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>

      <div class="section">
        <div class="section-title">📝 Notes & Observations</div>
        <div class="notes-box">
          ${session?.notes || ''}
        </div>
      </div>

      <div class="footer">
        <p style="margin-bottom: 15px;">
          <strong>Presenter:</strong> <span class="signature-line">${session?.presenter || ''}</span> 
          <strong>Date:</strong> <span class="signature-line"></span>
        </p>
        <p style="color: #6b7280; font-size: 9pt; margin-top: 20px;">
          Generated from WSafe AI Safety Platform<br/>
          © ${new Date().getFullYear()} WorkSafeAgent LLC | www.wsafeai.com
        </p>
      </div>
    </body>
    </html>
  `;
};

// Print Toolbox Talk
const printTalk = (talk, session = null) => {
  const printWindow = window.open('', '_blank');
  printWindow.document.write(generatePrintableHTML(talk, session));
  printWindow.document.close();
  printWindow.print();
};

// Download as text/PDF
const downloadPDF = (talk, session = null) => {
  const content = `
TOOLBOX TALK
============

Title: ${talk.title}
Duration: ${talk.duration || 10} minutes
Date: ${session?.date ? new Date(session.date).toLocaleDateString() : new Date().toLocaleDateString()}
${session ? `Presenter: ${session.presenter}
Location: ${session.location}` : ''}

DISCUSSION TOPICS
-----------------
${talk.topics?.map((t, i) => `${i + 1}. ${t}`).join('\n') || talk.content || 'No topics available'}

${talk.key_points?.length > 0 ? `
KEY POINTS TO REMEMBER
----------------------
${talk.key_points.map(p => `• ${p}`).join('\n')}
` : ''}

${talk.quiz_questions?.length > 0 ? `
QUIZ QUESTIONS
--------------
${talk.quiz_questions.map((q, i) => `Q${i + 1}: ${q.question}
A: ${q.answer}`).join('\n\n')}
` : ''}

ATTENDANCE SIGN-IN
------------------
Name                    | Employee ID    | Signature      | Date
------------------------|----------------|----------------|------------
${session?.attendees?.map(a => `${a.employee_name.padEnd(24)}| ${(a.employee_id || '-').padEnd(14)} | ✓              | ${new Date(session.date).toLocaleDateString()}`).join('\n') || ''}
                        |                |                |
                        |                |                |
                        |                |                |
                        |                |                |

${session?.notes ? `
NOTES
-----
${session.notes}
` : ''}

Presenter: _______________________ Date: _______________

Generated from WSafe AI Safety Platform
© ${new Date().getFullYear()} WorkSafeAgent LLC | www.wsafeai.com
`;

  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `toolbox-talk-${talk.title?.replace(/[^a-z0-9]/gi, '-').toLowerCase() || 'document'}.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Download as Word Doc
const downloadWord = (talk, session = null) => {
  const htmlContent = generatePrintableHTML(talk, session);
  const blob = new Blob([htmlContent], { type: 'application/msword' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `toolbox-talk-${talk.title?.replace(/[^a-z0-9]/gi, '-').toLowerCase() || 'document'}.doc`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const ToolboxTalksPage = () => {
  const [library, setLibrary] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTalk, setSelectedTalk] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [viewMode, setViewMode] = useState('details'); // 'details' or 'document'
  const [showSessionDialog, setShowSessionDialog] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [generatedTalk, setGeneratedTalk] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    topic: '',
    industry: 'general',
    weather_condition: '',
    specific_hazards: []
  });
  const [sessionForm, setSessionForm] = useState({
    talk_id: '',
    presenter: '',
    date: new Date().toISOString().split('T')[0],
    location: '',
    attendees: [],
    notes: ''
  });
  const [newAttendee, setNewAttendee] = useState({ employee_name: '', employee_id: '' });
  const { toast } = useToast();

  // Scheduler state
  const [schedules, setSchedules] = useState([]);
  const [schedulerSuggestions, setSchedulerSuggestions] = useState([]);
  const [schedulerLoading, setSchedulerLoading] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    talk_id: '',
    scheduled_date: new Date().toISOString().split('T')[0],
    scheduled_time: '09:00',
    presenter: '',
    location: '',
    recurrence: 'none',
    recurrence_end_date: '',
    notes: ''
  });
  const [autoSuggestions, setAutoSuggestions] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [libraryRes, sessionsRes, statsRes, suggestionsRes, schedulesRes] = await Promise.all([
        axios.get(`${API}/toolbox-talks/library`),
        axios.get(`${API}/toolbox-talks/sessions`),
        axios.get(`${API}/toolbox-talks/stats`),
        axios.get(`${API}/toolbox-talks/suggestions`),
        axios.get(`${API}/toolbox-talks/scheduler/upcoming?days=60`)
      ]);
      setLibrary(libraryRes.data.talks || []);
      setSessions(sessionsRes.data.sessions || []);
      setStats(statsRes.data);
      setSuggestions(suggestionsRes.data.suggestions || []);
      setSchedules(schedulesRes.data.upcoming || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Scheduler Functions
  const fetchAutoSuggestions = async () => {
    setSchedulerLoading(true);
    try {
      const response = await axios.post(`${API}/toolbox-talks/scheduler/auto-suggest`);
      setAutoSuggestions(response.data);
      toast({ title: 'Suggestions Generated', description: `Found ${response.data.suggestions?.length || 0} recommended talks` });
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      toast({ title: 'Error', description: 'Failed to generate suggestions', variant: 'destructive' });
    } finally {
      setSchedulerLoading(false);
    }
  };

  const handleAutoSchedule = async () => {
    setSchedulerLoading(true);
    try {
      const response = await axios.post(`${API}/toolbox-talks/scheduler/auto-schedule?count=4`);
      toast({ title: 'Auto-Scheduled!', description: `Scheduled ${response.data.scheduled?.length || 0} talks` });
      fetchData();
      setAutoSuggestions(null);
    } catch (error) {
      console.error('Error auto-scheduling:', error);
      toast({ title: 'Error', description: 'Failed to auto-schedule talks', variant: 'destructive' });
    } finally {
      setSchedulerLoading(false);
    }
  };

  const handleScheduleTalk = async () => {
    if (!scheduleForm.talk_id || !scheduleForm.scheduled_date) {
      toast({ title: 'Error', description: 'Please select a talk and date', variant: 'destructive' });
      return;
    }
    try {
      await axios.post(`${API}/toolbox-talks/scheduler`, scheduleForm);
      toast({ title: 'Scheduled!', description: 'Talk has been scheduled' });
      setShowScheduleDialog(false);
      setScheduleForm({
        talk_id: '',
        scheduled_date: new Date().toISOString().split('T')[0],
        scheduled_time: '09:00',
        presenter: '',
        location: '',
        recurrence: 'none',
        recurrence_end_date: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error scheduling talk:', error);
      toast({ title: 'Error', description: 'Failed to schedule talk', variant: 'destructive' });
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      await axios.delete(`${API}/toolbox-talks/scheduler/${scheduleId}`);
      toast({ title: 'Deleted', description: 'Schedule removed' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete schedule', variant: 'destructive' });
    }
  };

  const quickScheduleTalk = (talkId, talkTitle) => {
    setScheduleForm({
      ...scheduleForm,
      talk_id: talkId
    });
    setShowScheduleDialog(true);
  };

  const fetchTalkDetails = async (talkId) => {
    try {
      const response = await axios.get(`${API}/toolbox-talks/library/${talkId}`);
      setSelectedTalk(response.data);
      setViewMode('details');
    } catch (error) {
      console.error('Error fetching talk:', error);
    }
  };

  const handleGenerateTalk = async () => {
    if (!generateForm.topic) {
      toast({ title: 'Error', description: 'Please enter a topic', variant: 'destructive' });
      return;
    }
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/toolbox-talks/generate?topic=${encodeURIComponent(generateForm.topic)}&industry=${generateForm.industry}`);
      setGeneratedTalk(response.data.talk);
      toast({ title: 'Success', description: 'Toolbox talk generated!' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to generate talk', variant: 'destructive' });
    } finally {
      setGenerating(false);
    }
  };

  const handleCreateSession = async () => {
    if (!sessionForm.talk_id || !sessionForm.presenter || !sessionForm.location || sessionForm.attendees.length === 0) {
      toast({ title: 'Error', description: 'Please fill all required fields and add at least one attendee', variant: 'destructive' });
      return;
    }
    try {
      await axios.post(`${API}/toolbox-talks/sessions`, sessionForm);
      toast({ title: 'Success', description: 'Session recorded successfully!' });
      setShowSessionDialog(false);
      resetSessionForm();
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to record session', variant: 'destructive' });
    }
  };

  const addAttendee = () => {
    if (!newAttendee.employee_name) return;
    setSessionForm({
      ...sessionForm,
      attendees: [...sessionForm.attendees, { ...newAttendee, attended: true }]
    });
    setNewAttendee({ employee_name: '', employee_id: '' });
  };

  const removeAttendee = (index) => {
    setSessionForm({
      ...sessionForm,
      attendees: sessionForm.attendees.filter((_, i) => i !== index)
    });
  };

  const resetSessionForm = () => {
    setSessionForm({
      talk_id: '',
      presenter: '',
      date: new Date().toISOString().split('T')[0],
      location: '',
      attendees: [],
      notes: ''
    });
  };

  const getSeasonIcon = (season) => {
    const icons = {
      summer: Sun,
      winter: Snowflake,
      spring: Leaf,
      fall: Leaf,
      all: Calendar
    };
    const Icon = icons[season] || Calendar;
    return <Icon className="w-4 h-4" />;
  };

  if (loading && library.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/10 rounded-lg">
              <BookOpen className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">Toolbox Talks</h1>
              <p className="text-sm text-slate-300">Safety meetings with attendance tracking</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Button onClick={fetchData} variant="outline" className="text-white border-white/20 hover:bg-white/10">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
              <DialogTrigger asChild>
                <Button className="bg-purple-500 hover:bg-purple-600">
                  <Sparkles className="w-4 h-4 mr-2" /> AI Generate
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Generate AI Toolbox Talk</DialogTitle>
                  <DialogDescription>Create a custom safety talk with AI</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Topic *</Label>
                    <Input value={generateForm.topic} onChange={e => setGenerateForm({...generateForm, topic: e.target.value})} placeholder="e.g., Ladder safety, Chemical handling" />
                  </div>
                  <div className="space-y-2">
                    <Label>Industry</Label>
                    <Select value={generateForm.industry} onValueChange={v => setGenerateForm({...generateForm, industry: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General</SelectItem>
                        <SelectItem value="construction">Construction</SelectItem>
                        <SelectItem value="manufacturing">Manufacturing</SelectItem>
                        <SelectItem value="healthcare">Healthcare</SelectItem>
                        <SelectItem value="warehousing">Warehousing</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Weather Condition (optional)</Label>
                    <Input value={generateForm.weather_condition} onChange={e => setGenerateForm({...generateForm, weather_condition: e.target.value})} placeholder="e.g., Hot, Cold, Rainy" />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>Cancel</Button>
                  <Button onClick={handleGenerateTalk} disabled={generating}>
                    {generating ? 'Generating...' : 'Generate Talk'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <Dialog open={showSessionDialog} onOpenChange={setShowSessionDialog}>
              <DialogTrigger asChild>
                <Button className="bg-green-500 hover:bg-green-600">
                  <Plus className="w-4 h-4 mr-2" /> Record Session
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Record Toolbox Talk Session</DialogTitle>
                  <DialogDescription>Log attendance for a safety meeting</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Select Talk *</Label>
                    <Select value={sessionForm.talk_id} onValueChange={v => setSessionForm({...sessionForm, talk_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Choose a toolbox talk" /></SelectTrigger>
                      <SelectContent>
                        {library.map(t => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Presenter *</Label>
                      <Input value={sessionForm.presenter} onChange={e => setSessionForm({...sessionForm, presenter: e.target.value})} placeholder="Presenter name" />
                    </div>
                    <div className="space-y-2">
                      <Label>Date</Label>
                      <Input type="date" value={sessionForm.date} onChange={e => setSessionForm({...sessionForm, date: e.target.value})} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Location *</Label>
                    <Input value={sessionForm.location} onChange={e => setSessionForm({...sessionForm, location: e.target.value})} placeholder="Where was the meeting held?" />
                  </div>
                  <div className="space-y-2">
                    <Label>Attendees ({sessionForm.attendees.length}) *</Label>
                    <div className="flex gap-2">
                      <Input value={newAttendee.employee_name} onChange={e => setNewAttendee({...newAttendee, employee_name: e.target.value})} placeholder="Employee name" className="flex-1" />
                      <Input value={newAttendee.employee_id} onChange={e => setNewAttendee({...newAttendee, employee_id: e.target.value})} placeholder="ID (optional)" className="w-32" />
                      <Button type="button" onClick={addAttendee}><Plus className="w-4 h-4" /></Button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {sessionForm.attendees.map((a, i) => (
                        <Badge key={i} variant="secondary" className="cursor-pointer" onClick={() => removeAttendee(i)}>
                          {a.employee_name} {a.employee_id && `(${a.employee_id})`} ×
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea value={sessionForm.notes} onChange={e => setSessionForm({...sessionForm, notes: e.target.value})} placeholder="Additional notes" rows={2} />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowSessionDialog(false)}>Cancel</Button>
                  <Button onClick={handleCreateSession}>Record Session</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Total Sessions</p>
                  <p className="text-2xl font-bold">{stats.total_sessions}</p>
                </div>
                <FileText className="w-6 h-6 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Total Attendance</p>
                  <p className="text-2xl font-bold">{stats.total_attendance}</p>
                </div>
                <Users className="w-6 h-6 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">This Month</p>
                  <p className="text-2xl font-bold">{stats.sessions_this_month}</p>
                </div>
                <Calendar className="w-6 h-6 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Avg. Attendance</p>
                  <p className="text-2xl font-bold">{stats.average_attendance}</p>
                </div>
                <CheckCircle className="w-6 h-6 text-orange-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" /> Suggested for Today
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 overflow-x-auto pb-2">
              {suggestions.map(s => (
                <div key={s.id} className="min-w-[200px] p-3 bg-white rounded-lg border cursor-pointer hover:shadow-md" onClick={() => fetchTalkDetails(s.id)}>
                  <p className="font-medium text-sm">{s.title}</p>
                  <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                    <Clock className="w-3 h-3" /> {s.duration} min
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {s.reasons?.map((r, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{r}</Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="library" className="space-y-4">
        <TabsList>
          <TabsTrigger value="library">Talk Library</TabsTrigger>
          <TabsTrigger value="scheduler">
            <CalendarDays className="w-4 h-4 mr-1" /> Scheduler
          </TabsTrigger>
          <TabsTrigger value="sessions">Session History</TabsTrigger>
        </TabsList>

        <TabsContent value="library">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {library.map(talk => (
              <Card key={talk.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => fetchTalkDetails(talk.id)}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-base">{talk.title}</CardTitle>
                    <div className="flex items-center gap-1">
                      {getSeasonIcon(talk.season)}
                      {talk.weather_relevant && <Cloud className="w-4 h-4 text-blue-400" />}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{talk.duration} min</span>
                    <span className="flex items-center gap-1"><FileText className="w-4 h-4" />{talk.topics_count} topics</span>
                    {talk.has_quiz && <Badge variant="secondary">Quiz</Badge>}
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button className="flex-1" variant="outline" onClick={(e) => { e.stopPropagation(); setSessionForm({...sessionForm, talk_id: talk.id}); setShowSessionDialog(true); }}>
                      <Play className="w-4 h-4 mr-1" /> Present
                    </Button>
                    <Button variant="outline" onClick={(e) => { e.stopPropagation(); quickScheduleTalk(talk.id, talk.title); }}>
                      <CalendarDays className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* SCHEDULER TAB */}
        <TabsContent value="scheduler">
          <div className="space-y-6">
            {/* Scheduler Header */}
            <Card className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white border-0">
              <CardContent className="py-6">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-white/20 rounded-lg">
                      <Wand2 className="w-8 h-8" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold">Smart Scheduler</h2>
                      <p className="text-indigo-100 text-sm">AI-powered scheduling based on season, weather & injury history</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button 
                      onClick={fetchAutoSuggestions} 
                      disabled={schedulerLoading}
                      className="bg-white text-indigo-600 hover:bg-indigo-50"
                    >
                      {schedulerLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                      Get AI Suggestions
                    </Button>
                    <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
                      <DialogTrigger asChild>
                        <Button className="bg-white/20 hover:bg-white/30 text-white border-white/30" variant="outline">
                          <Plus className="w-4 h-4 mr-2" /> Manual Schedule
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Schedule a Toolbox Talk</DialogTitle>
                          <DialogDescription>Plan your safety meeting</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label>Select Talk *</Label>
                            <Select value={scheduleForm.talk_id} onValueChange={v => setScheduleForm({...scheduleForm, talk_id: v})}>
                              <SelectTrigger><SelectValue placeholder="Choose a talk" /></SelectTrigger>
                              <SelectContent>
                                {library.map(t => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Date *</Label>
                              <Input type="date" value={scheduleForm.scheduled_date} onChange={e => setScheduleForm({...scheduleForm, scheduled_date: e.target.value})} />
                            </div>
                            <div className="space-y-2">
                              <Label>Time</Label>
                              <Input type="time" value={scheduleForm.scheduled_time} onChange={e => setScheduleForm({...scheduleForm, scheduled_time: e.target.value})} />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Presenter</Label>
                              <Input value={scheduleForm.presenter} onChange={e => setScheduleForm({...scheduleForm, presenter: e.target.value})} placeholder="Who will present?" />
                            </div>
                            <div className="space-y-2">
                              <Label>Location</Label>
                              <Input value={scheduleForm.location} onChange={e => setScheduleForm({...scheduleForm, location: e.target.value})} placeholder="Where?" />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Recurrence</Label>
                              <Select value={scheduleForm.recurrence} onValueChange={v => setScheduleForm({...scheduleForm, recurrence: v})}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">No Recurrence</SelectItem>
                                  <SelectItem value="weekly">Weekly</SelectItem>
                                  <SelectItem value="biweekly">Every 2 Weeks</SelectItem>
                                  <SelectItem value="monthly">Monthly</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            {scheduleForm.recurrence !== 'none' && (
                              <div className="space-y-2">
                                <Label>End Date</Label>
                                <Input type="date" value={scheduleForm.recurrence_end_date} onChange={e => setScheduleForm({...scheduleForm, recurrence_end_date: e.target.value})} />
                              </div>
                            )}
                          </div>
                          <div className="space-y-2">
                            <Label>Notes</Label>
                            <Textarea value={scheduleForm.notes} onChange={e => setScheduleForm({...scheduleForm, notes: e.target.value})} placeholder="Any additional notes?" rows={2} />
                          </div>
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>Cancel</Button>
                          <Button onClick={handleScheduleTalk}>Schedule Talk</Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* AI Suggestions Panel */}
            {autoSuggestions && (
              <Card className="border-2 border-purple-200 bg-purple-50">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Target className="w-5 h-5 text-purple-600" /> AI-Recommended Schedule
                    </CardTitle>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleAutoSchedule} disabled={schedulerLoading} className="bg-purple-600 hover:bg-purple-700">
                        <Wand2 className="w-4 h-4 mr-2" /> Auto-Schedule Top 4
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setAutoSuggestions(null)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <CardDescription>
                    Based on: {autoSuggestions.analysis?.current_season} season
                    {Object.keys(autoSuggestions.analysis?.injury_patterns || {}).length > 0 && 
                      ` • ${Object.values(autoSuggestions.analysis.injury_patterns).reduce((a,b) => a+b, 0)} recent injuries analyzed`}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {autoSuggestions.suggestions?.slice(0, 6).map((s, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                            s.priority === 'high' ? 'bg-red-500' : s.priority === 'medium' ? 'bg-amber-500' : 'bg-green-500'
                          }`}>
                            {i + 1}
                          </div>
                          <div>
                            <p className="font-medium">{s.title}</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {s.reasons?.slice(0, 2).map((r, j) => (
                                <Badge key={j} variant="outline" className="text-xs">{r}</Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={`${
                            s.priority === 'high' ? 'bg-red-100 text-red-800' : 
                            s.priority === 'medium' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'
                          }`}>
                            {s.priority}
                          </Badge>
                          <span className="text-sm text-gray-500">{s.suggested_date}</span>
                          <Button size="sm" variant="outline" onClick={() => {
                            setScheduleForm({...scheduleForm, talk_id: s.talk_id, scheduled_date: s.suggested_date});
                            setShowScheduleDialog(true);
                          }}>
                            <CalendarDays className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Upcoming Scheduled Talks */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-600" /> Upcoming Scheduled Talks
                </CardTitle>
                <CardDescription>Your planned safety meetings for the next 60 days</CardDescription>
              </CardHeader>
              <CardContent>
                {schedules.length > 0 ? (
                  <div className="space-y-3">
                    {schedules.map((schedule) => (
                      <div key={schedule.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border hover:bg-gray-100 transition-colors">
                        <div className="flex items-center gap-4">
                          <div className="text-center min-w-[60px]">
                            <div className="text-2xl font-bold text-blue-600">
                              {new Date(schedule.scheduled_date).getDate()}
                            </div>
                            <div className="text-xs text-gray-500 uppercase">
                              {new Date(schedule.scheduled_date).toLocaleDateString('en-US', { month: 'short' })}
                            </div>
                          </div>
                          <div>
                            <p className="font-medium">{schedule.talk_title || schedule.talk_id}</p>
                            <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {schedule.scheduled_time || '09:00'}
                              </span>
                              {schedule.presenter && (
                                <span className="flex items-center gap-1">
                                  <Users className="w-3 h-3" /> {schedule.presenter}
                                </span>
                              )}
                              {schedule.location && (
                                <span>{schedule.location}</span>
                              )}
                            </div>
                            {schedule.auto_suggested && (
                              <Badge variant="outline" className="mt-2 text-xs text-purple-600 border-purple-200">
                                <Sparkles className="w-3 h-3 mr-1" /> AI Suggested
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            size="sm" 
                            onClick={() => {
                              setSessionForm({...sessionForm, talk_id: schedule.talk_id});
                              setShowSessionDialog(true);
                            }}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Play className="w-4 h-4 mr-1" /> Start
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => handleDeleteSchedule(schedule.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CalendarDays className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">No talks scheduled yet</p>
                    <div className="flex justify-center gap-3">
                      <Button onClick={fetchAutoSuggestions} disabled={schedulerLoading}>
                        <Sparkles className="w-4 h-4 mr-2" /> Get AI Suggestions
                      </Button>
                      <Button variant="outline" onClick={() => setShowScheduleDialog(true)}>
                        <Plus className="w-4 h-4 mr-2" /> Schedule Manually
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="sessions">
          <Card>
            <CardContent className="p-0">
              <div className="divide-y">
                {sessions.map(session => (
                  <div 
                    key={session.id} 
                    className="p-4 hover:bg-gray-50 cursor-pointer"
                    onClick={() => { setSelectedSession(session); setViewMode('details'); }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{session.talk_title || session.talk_id}</p>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                          <span>Presenter: {session.presenter}</span>
                          <span>•</span>
                          <span>{new Date(session.date).toLocaleDateString()}</span>
                          <span>•</span>
                          <span>{session.location}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-green-100 text-green-800">
                          <Users className="w-3 h-3 mr-1" /> {session.attendance_count} attended
                        </Badge>
                        <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); printTalk({ title: session.talk_title || session.talk_id, topics: [], ...session }, session); }}>
                          <Printer className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {sessions.length === 0 && (
                  <div className="p-12 text-center">
                    <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No sessions recorded yet</p>
                    <Button className="mt-4" onClick={() => setShowSessionDialog(true)}>
                      <Plus className="w-4 h-4 mr-2" /> Record First Session
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Talk Detail Dialog */}
      <Dialog open={!!selectedTalk} onOpenChange={() => setSelectedTalk(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedTalk && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-blue-600" />
                  {selectedTalk.title}
                </DialogTitle>
                <DialogDescription className="flex items-center gap-2">
                  <Clock className="w-4 h-4" /> {selectedTalk.duration} minutes
                  {selectedTalk.has_quiz && <Badge variant="secondary" className="ml-2">Has Quiz</Badge>}
                </DialogDescription>
              </DialogHeader>
              
              {/* View Mode Toggle & Export Buttons */}
              <div className="flex items-center justify-between border-b pb-4">
                <div className="flex gap-2">
                  <Button 
                    variant={viewMode === 'details' ? 'default' : 'outline'} 
                    size="sm" 
                    onClick={() => setViewMode('details')}
                  >
                    <Eye className="w-4 h-4 mr-2" /> Details
                  </Button>
                  <Button 
                    variant={viewMode === 'document' ? 'default' : 'outline'} 
                    size="sm" 
                    onClick={() => setViewMode('document')}
                  >
                    <FileText className="w-4 h-4 mr-2" /> Document
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => printTalk(selectedTalk)}>
                    <Printer className="w-4 h-4 mr-2" /> Print
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => downloadPDF(selectedTalk)}>
                    <Download className="w-4 h-4 mr-2" /> PDF
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => downloadWord(selectedTalk)}>
                    <FileDown className="w-4 h-4 mr-2" /> Word
                  </Button>
                  <QRShareButton
                    documentType="toolbox"
                    documentId={selectedTalk?.id}
                    documentTitle={selectedTalk?.title || 'Toolbox Talk'}
                    variant="outline"
                    size="sm"
                  />
                </div>
              </div>

              {viewMode === 'details' ? (
                /* Details View */
                <div className="space-y-6 py-4">
                  <div>
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" /> Discussion Topics
                    </h4>
                    <ul className="space-y-2">
                      {selectedTalk.topics?.map((topic, i) => (
                        <li key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                          <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">{i + 1}</span>
                          <span className="text-sm">{topic}</span>
                        </li>
                      )) || <li className="text-sm text-gray-500">{selectedTalk.content || 'No topics available'}</li>}
                    </ul>
                  </div>
                  
                  {selectedTalk.key_points?.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-3">🎯 Key Points</h4>
                      <ul className="space-y-2">
                        {selectedTalk.key_points.map((point, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <span className="text-green-500">✓</span> {point}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {selectedTalk.quiz_questions?.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-3">❓ Quiz Questions</h4>
                      <div className="space-y-3">
                        {selectedTalk.quiz_questions.map((q, i) => (
                          <div key={i} className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                            <p className="font-medium text-sm">Q{i + 1}: {q.question}</p>
                            <p className="text-sm text-green-600 mt-2">✓ {q.answer}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex gap-2 pt-4 border-t">
                    <Button className="flex-1" onClick={() => { setSessionForm({...sessionForm, talk_id: selectedTalk.id}); setSelectedTalk(null); setShowSessionDialog(true); }}>
                      <Play className="w-4 h-4 mr-2" /> Present This Talk
                    </Button>
                  </div>
                </div>
              ) : (
                /* Document View - Full Printable Document */
                <div className="border rounded-lg p-6 bg-white">
                  <div className="text-center border-b-2 border-blue-600 pb-4 mb-6">
                    <h2 className="text-2xl font-bold text-blue-600">🛡️ TOOLBOX TALK</h2>
                    <p className="text-lg text-gray-700">{selectedTalk.title}</p>
                  </div>

                  <div className="flex justify-around bg-gray-50 p-4 rounded-lg mb-6">
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Duration</div>
                      <div className="font-bold text-blue-600">{selectedTalk.duration || 10} min</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Topics</div>
                      <div className="font-bold text-blue-600">{selectedTalk.topics?.length || 0}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Date</div>
                      <div className="font-bold text-blue-600">{new Date().toLocaleDateString()}</div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-lg font-bold text-blue-600 border-b pb-2 mb-3">📋 Discussion Topics</h3>
                    {selectedTalk.topics?.map((topic, i) => (
                      <div key={i} className="p-3 bg-blue-50 rounded-lg mb-2 border-l-4 border-blue-500">
                        <span className="inline-block w-6 h-6 bg-blue-600 text-white rounded-full text-center text-sm font-bold mr-2">{i + 1}</span>
                        {topic}
                      </div>
                    )) || <p>{selectedTalk.content || 'No topics available'}</p>}
                  </div>

                  {selectedTalk.quiz_questions?.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-bold text-blue-600 border-b pb-2 mb-3">❓ Quiz Questions</h3>
                      {selectedTalk.quiz_questions.map((q, i) => (
                        <div key={i} className="p-3 bg-yellow-50 rounded-lg mb-2 border border-yellow-200">
                          <p className="font-bold">Q{i + 1}: {q.question}</p>
                          <p className="text-green-600 mt-1">✓ {q.answer}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mb-6">
                    <h3 className="text-lg font-bold text-blue-600 border-b pb-2 mb-3">✍️ Attendance Sign-In</h3>
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="border p-2 text-left w-8">#</th>
                          <th className="border p-2 text-left">Name</th>
                          <th className="border p-2 text-left">Employee ID</th>
                          <th className="border p-2 text-left">Signature</th>
                          <th className="border p-2 text-left">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[1,2,3,4,5,6,7,8,9,10].map(i => (
                          <tr key={i}>
                            <td className="border p-2 h-10">{i}</td>
                            <td className="border p-2"></td>
                            <td className="border p-2"></td>
                            <td className="border p-2"></td>
                            <td className="border p-2"></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-lg font-bold text-blue-600 border-b pb-2 mb-3">📝 Notes</h3>
                    <div className="border rounded-lg p-4 min-h-[80px] bg-gray-50"></div>
                  </div>

                  <div className="border-t-2 pt-4 text-center text-gray-500 text-sm">
                    <p className="mb-4">
                      <strong>Presenter:</strong> _________________ 
                      <strong className="ml-8">Date:</strong> _________________
                    </p>
                    <p>Generated from WSafe AI Safety Platform</p>
                    <p>© {new Date().getFullYear()} WorkSafeAgent LLC | www.wsafeai.com</p>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Session Detail Dialog */}
      <Dialog open={!!selectedSession} onOpenChange={() => setSelectedSession(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedSession && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Session Record
                </DialogTitle>
                <DialogDescription>
                  {selectedSession.talk_title || selectedSession.talk_id} • {new Date(selectedSession.date).toLocaleDateString()}
                </DialogDescription>
              </DialogHeader>
              
              {/* Export Buttons */}
              <div className="flex justify-end gap-2 border-b pb-4">
                <Button variant="outline" size="sm" onClick={() => printTalk({ title: selectedSession.talk_title || selectedSession.talk_id, topics: [], duration: 10 }, selectedSession)}>
                  <Printer className="w-4 h-4 mr-2" /> Print
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadPDF({ title: selectedSession.talk_title || selectedSession.talk_id, topics: [], duration: 10 }, selectedSession)}>
                  <Download className="w-4 h-4 mr-2" /> PDF
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadWord({ title: selectedSession.talk_title || selectedSession.talk_id, topics: [], duration: 10 }, selectedSession)}>
                  <FileDown className="w-4 h-4 mr-2" /> Word
                </Button>
              </div>

              <div className="space-y-4 py-4">
                {/* Session Info */}
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div><strong>Presenter:</strong> {selectedSession.presenter}</div>
                      <div><strong>Location:</strong> {selectedSession.location}</div>
                      <div><strong>Date:</strong> {new Date(selectedSession.date).toLocaleDateString()}</div>
                      <div><strong>Attendees:</strong> {selectedSession.attendance_count || selectedSession.attendees?.length || 0}</div>
                    </div>
                  </CardContent>
                </Card>

                {/* Attendees */}
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4" /> Attendees
                  </h4>
                  <div className="space-y-2">
                    {selectedSession.attendees?.map((a, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          {a.employee_name}
                          {a.employee_id && <span className="text-gray-500 text-sm">({a.employee_id})</span>}
                        </span>
                        <Badge className="bg-green-100 text-green-800">Present</Badge>
                      </div>
                    )) || <p className="text-gray-500">No attendance records</p>}
                  </div>
                </div>

                {/* Notes */}
                {selectedSession.notes && (
                  <div>
                    <h4 className="font-medium mb-3">📝 Notes</h4>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      {selectedSession.notes}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Generated Talk Dialog */}
      <Dialog open={!!generatedTalk} onOpenChange={() => setGeneratedTalk(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {generatedTalk && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-500" />
                  {generatedTalk.title}
                </DialogTitle>
              </DialogHeader>
              <div className="py-4">
                <div className="prose prose-sm max-w-none whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">
                  {generatedTalk.content}
                </div>
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" className="flex-1" onClick={() => printTalk(generatedTalk)}>
                    <Printer className="w-4 h-4 mr-2" /> Print
                  </Button>
                  <Button variant="outline" className="flex-1" onClick={() => downloadPDF(generatedTalk)}>
                    <Download className="w-4 h-4 mr-2" /> PDF
                  </Button>
                  <Button variant="outline" className="flex-1" onClick={() => downloadWord(generatedTalk)}>
                    <FileDown className="w-4 h-4 mr-2" /> Word
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ToolboxTalksPage;
